// -----------------------------------------------------------------
// <copyright                                                       
//   file="Izenda.Validator.js"                            
//   company="Izenda">                                              
//  Copyright (c) 2015 Izenda, Inc.                                 
//  ALL RIGHTS RESERVED                                             
//                                                                  
//  The entire contents of this file is protected by U.S. and       
//  International Copyright Laws. Unauthorized reproduction,        
//  reverse-engineering, and distribution of all or any portion of  
//  the code contained in this file is strictly prohibited and may  
//  result in severe civil and criminal penalties and will be       
//  prosecuted to the maximum extent possible under the law.        
//                                                                  
//  RESTRICTIONS                                                    
//                                                                  
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES           
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE                          
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS              
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET    
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.                 
//                                                                  
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED      
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE        
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE       
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT  
//  AND PERMISSION FROM IZENDA INC.                                 
//                                                                  
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON  
//  ADDITIONAL RESTRICTIONS.                                        
// </copyright>                                                     
// -----------------------------------------------------------------

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import classnames from 'classnames';
import 'share/utils/Izenda.StringUtilities';
import TooltipUtils from 'share/utils/Izenda.TooltipUtils';

const { get, isArray, join, omit, assign, isEmpty, filter, flow, map } = require('lodash/fp');

/**
 * A React higher order compoment that enhances the validation to target component
 * @param  {Object} options {tooltipPosition, getValue}
 * @return {Function}       A function that accept a target component.
 */
export default function Validator(options) {
  return function (EditComponent) {
    @observer
    class ValidatableEditor extends Component {

      static displayName = 'Validator(' + EditComponent.displayName + ')';

      static contextTypes = {
        validation: PropTypes.object.isRequired
      };

      static propTypes = {
        field: PropTypes.string.isRequired,
        validations: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
        group: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        onValidated: PropTypes.func,
        onReset: PropTypes.func,
        shouldInjectStateToChildren: PropTypes.bool,
        tooltipPosition: PropTypes.string
      };

      static defaultProps = {
        field: 'ValidatableEditor',
        shouldInjectStateToChildren: false,
        tooltipPosition: get('tooltipPosition', options) || 'top'
      };

      state = {
        isPristine: true, // Indicate fresh state without change and unvalidated.
        isUnvalidatedChange: false, // Indicate value has been changed but unvalidated
        error: false, // Indicate validated failed
        messages: [] // Validation error messages
      };

      _isMounted = false;
      hasTooltip = false;

      /**
       * Return whether the validator state has changed
       * @param  {Object}  currState Current validator state
       * @param  {Object}  prevState Previous validator state
       */
      static isValidationStateChanged(currState, prevState) {
        return (
          currState.isPristine !== prevState.isPristine ||
          currState.error !== prevState.error ||
          currState.messages.length !== prevState.messages.length ||
          currState.messages[0] !== prevState.messages[0]
        );
      }

      componentDidMount() {
        const { props } = this;
        this._isMounted = true;
        this.context.validation.attachComponent(this);

        let title = this.state.messages[0];
        if (isArray(this.state.messages[0]) && this.state.messages[0].length > 1) {
          title = flow(map(m => `- ${m}`), join('\n'))(this.state.messages[0]);
        }

        const tooltipOption = {
          title: title,
          trigger: 'hover',
          placement: 'auto ' + props.tooltipPosition,
          template:
            '<div class="tooltip" role="tooltip"><div class="tooltip-arrow bg-errors"></div><div class="tooltip-inner bg-errors"></div></div>'
        };
        TooltipUtils.initTooltip(this.refs.editor, tooltipOption);
        TooltipUtils.disableTooltip(this.refs.editor);
      }

      componentDidUpdate(prevProps, prevState) {
        if (ValidatableEditor.isValidationStateChanged(this.state, prevState)) {
          if (!this.state.isPristine && this.state.error) {
            let title = this.state.messages[0];
            if (isArray(this.state.messages[0]) && this.state.messages[0].length > 1) {
              title = flow(map(mess => '- ' + mess), join('\n'))(this.state.messages[0]);
            }
            TooltipUtils.updateTooltip(this.refs.editor, title);
          } else {
            TooltipUtils.disableTooltip(this.refs.editor);
          }
        }
      }

      componentWillUnmount() {
        this.context.validation.detachComponent(this);
        TooltipUtils.destroyTooltip(this.refs.editor);
        this._isMounted = false;
      }

      render() {
        const { props, state } = this;
        const { shouldInjectStateToChildren } = props;

        const wrappedOnChange = function() {
          !state.isPristine && this.setUnvalidatedChange();
          return props.onChange ? props.onChange.apply(null, arguments) : null;
        }.bind(this);

        let rest = omit([
          'field',
          'group',
          'validations',
          'className',
          'onChange',
          'onValidated',
          'onReset',
          'shouldInjectStateToChildren',
          'tooltipPosition'
        ], props);
        if (shouldInjectStateToChildren) {
          rest = assign({
            validateInfo: this.state
          }, rest);
        }

        const classes = classnames(props.className, 'validator ', {
          unvalidated: !this.state.isPristine && this.state.isUnvalidatedChange,
          errors: !this.state.isPristine && !this.state.isUnvalidatedChange && this.state.error,
          success: !this.state.isPristine && !this.state.isUnvalidatedChange && !this.state.error
        });

        const key = this.context.validation.getKey(this);

        return (
          <div ref="container" id={String.hashKeyId(key)} className={props.containerClassName}>
            <EditComponent
              className={classes}
              {...rest}
              onChange={wrappedOnChange}
              ref="editor"
              data-toggle="tooltip"
            />
          </div>
        );
      }

      /**
       * Get component value that will be used to validate
       */
      getValue() {
        return options && options.getValue ? options.getValue(this.refs.editor) : this.refs.editor.props.value;
      }

      /**
       * Set pristine state of validator. By calling this, the validator
       * will reset its state.
       */
      setPristine() {
        const { props } = this;
        props.onReset && props.onReset();
        this._isMounted &&
        this.setState({
          isPristine: true,
          isUnvalidatedChange: false,
          error: false,
          messages: []
        });
      }

      /**
       * Set error state including its error messages. The validator will show
       * error indicator and error messages.
       */
      setError(msgs) {
        const { props } = this;
        props.onValidated && props.onValidated(false);
        this._isMounted &&
        this.setState({
          isPristine: false,
          isUnvalidatedChange: false,
          error: true,
          messages: this.getErrorMessages(toJS(msgs))
        });
      }

      /**
       * Get messages from list errors
       */
      getErrorMessages(errorObject) {
        const result = [];

        const clientErrorMessages = filter(o => typeof o === 'string', errorObject);
        if (clientErrorMessages.length > 0)
          return clientErrorMessages;

        errorObject.map(error => {
          error.messages && result.push(error.messages);
        });
        return isEmpty(result) ? null : result;
      }

      /**
       * Set success state. The validator will show success indicator.
       */
      setSuccess() {
        const { props } = this;
        props.onValidated && props.onValidated(true);
        this._isMounted &&
        this.setState({
          isPristine: false,
          isUnvalidatedChange: false,
          error: false,
          messages: []
        });
      }

      /**
       * Set unvalidated changed state
       */
      setUnvalidatedChange() {
        this._isMounted &&
        this.setState({
          isPristine: false,
          isUnvalidatedChange: true,
          error: false,
          messages: []
        });
      }
    }

    return ValidatableEditor;
  };
}



// WEBPACK FOOTER //
// ./ui/share/components/hoc/Izenda.Validator.js